import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchTypesForBrand, fetchBrandById } from "../api/BrandAPIs";
import { createType, updateType, deleteType } from "../api/TypeAPIs";
import { colors } from "../constants/colors";
import {
  ChevronRight,
  Package,
  Edit,
  Trash2,
  Plus,
  Save,
  X,
  Layers,
  Eye,
  Building2,
  AlertCircle,
  Tag,
  TrendingUp,
  CheckCircle,
  XCircle,
  RefreshCw,
  Filter,
  Search,
  ChevronUp
} from "lucide-react";

const SkeletonLoader = ({ type }) => {
  if (type === "table") {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded mb-4"></div>
        {[...Array(3)].map((_, index) => (
          <div key={index} className="flex gap-4 border-b py-3">
            <div className="w-12 h-12 bg-gray-200 rounded"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const TypesPage = () => {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const [types, setTypes] = useState([]);
  const [brandData, setBrandData] = useState(null);
  const [newType, setNewType] = useState(null);
  const [editingType, setEditingType] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const [brandResponse, typesResponse] = await Promise.all([
          fetchBrandById(brandId),
          fetchTypesForBrand(brandId)
        ]);

        setBrandData(brandResponse);

        if (typesResponse && typesResponse.length > 0) {
          const processedTypes = typesResponse
            .map((type) => ({
              id: type.typeId,
              name: type.name,
              sales: type.sales || 0,
              isAvailable: type.isAvailable !== undefined ? type.isAvailable : true,
              brandId: parseInt(brandId)
            }))
            .sort((a, b) => b.sales - a.sales);
          setTypes(processedTypes);
        } else {
          setTypes([]);
        }
      } catch (err) {
        console.error("Error loading data:", err);
        setError(err.message || "فشل في تحميل البيانات");
      } finally {
        setIsLoading(false);
      }
    };

    if (brandId) {
      loadData();
    }
  }, [brandId]);

  const handleInputChange = (e, setState, state) => {
    const { name, value, type: inputType, checked } = e.target;
    setState((prevState) => ({
      ...prevState,
      [name]: inputType === "checkbox" ? checked : value,
    }));
  };

  const handleCreateType = async (e) => {
    e.preventDefault();
    try {
      const createPayload = {
        ...newType,
        brandId: parseInt(brandId)
      };
      const created = await createType(createPayload);
      
      const newTypeItem = {
        id: created.typeId,
        name: created.name,
        sales: created.sales || 0,
        isAvailable: created.isAvailable !== undefined ? created.isAvailable : true,
        brandId: parseInt(brandId)
      };
      
      setTypes(prevTypes => 
        [...prevTypes, newTypeItem].sort((a, b) => b.sales - a.sales)
      );
      setNewType(null);
    } catch (err) {
      console.error("Error creating type:", err);
      alert("فشل في إنشاء النوع: " + err.message);
    }
  };

  const handleUpdateType = async (e) => {
    e.preventDefault();
    try {
      const updatePayload = {
        ...editingType,
        brandId: parseInt(brandId)
      };
      await updateType(editingType.id, updatePayload);
      
      setTypes(prevTypes =>
        prevTypes.map(type =>
          type.id === editingType.id
            ? {
                ...type,
                name: editingType.name,
                isAvailable: editingType.isAvailable,
                brandId: parseInt(brandId)
              }
            : type
        ).sort((a, b) => b.sales - a.sales)
      );
      setEditingType(null);
    } catch (err) {
      console.error("Error updating type:", err);
      alert("فشل في تحديث النوع: " + err.message);
    }
  };

  const handleToggleAvailability = async (type) => {
    try {
      const updatedTypePayload = { 
        ...type, 
        isAvailable: !type.isAvailable,
        brandId: parseInt(brandId)
      };
      await updateType(type.id, updatedTypePayload);
      
      setTypes(prevTypes =>
        prevTypes.map(t => 
          t.id === type.id
            ? { ...t, isAvailable: !t.isAvailable }
            : t
        )
      );
    } catch (err) {
      console.error("Error updating type availability:", err);
      alert("فشل في تحديث حالة التوافر: " + err.message);
    }
  };

  const handleDeleteType = async (id) => {
    if (window.confirm("هل أنت متأكد أنك تريد حذف هذا النوع؟")) {
      try {
        await deleteType(id);
        setTypes(prevTypes => prevTypes.filter((type) => type.id !== id));
      } catch (err) {
        console.error("Error deleting type:", err);
        alert("فشل في حذف النوع: " + err.message);
      }
    }
  };

  const refreshData = async () => {
    try {
      setIsLoading(true);
      const typesResponse = await fetchTypesForBrand(brandId);
      
      if (typesResponse && typesResponse.length > 0) {
        const processedTypes = typesResponse
          .map((type) => ({
            id: type.typeId,
            name: type.name,
            sales: type.sales || 0,
            isAvailable: type.isAvailable !== undefined ? type.isAvailable : true,
            brandId: parseInt(brandId)
          }))
          .sort((a, b) => b.sales - a.sales);
        setTypes(processedTypes);
      } else {
        setTypes([]);
      }
    } catch (err) {
      console.error("Error refreshing data:", err);
      setError(err.message || "فشل في تحديث البيانات");
    } finally {
      setIsLoading(false);
    }
  };

  // Filter types based on search term
  const filteredTypes = types.filter(type => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      type.name.toLowerCase().includes(term) ||
      type.id.toString().includes(term)
    );
  });

  if (!brandId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4" dir="rtl">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-16 text-center">
            <AlertCircle className="mx-auto mb-4 text-red-500" size={64} />
            <h1 className="text-2xl font-bold text-red-600">خطأ في التحميل</h1>
            <p className="mt-2 text-gray-600">معرف العلامة التجارية مفقود</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 md:p-8" dir="rtl">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-8">
          <div className="flex items-center gap-3">
            <Layers className="text-purple-600" size={32} />
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              أنواع {brandData ? brandData.name : "جاري التحميل..."}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={refreshData}
              disabled={isLoading}
              className="flex items-center gap-2 bg-white/80 backdrop-blur-sm text-gray-700 px-4 py-2 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200 disabled:opacity-50"
            >
              <RefreshCw size={18} className={isLoading ? "animate-spin" : ""} />
              تحديث
            </button>
            <button
              onClick={() => navigate("/brands")}
              className="flex items-center gap-2 bg-white/80 backdrop-blur-sm text-gray-700 px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200"
            >
              <ChevronRight size={20} />
              العودة للعلامات التجارية
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-2xl mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertCircle className="text-red-500" size={24} />
              <p>{error}</p>
            </div>
            <button
              onClick={refreshData}
              className="text-red-600 hover:text-red-800 text-sm font-bold"
            >
              إعادة المحاولة
            </button>
          </div>
        )}

        {/* Stats and Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Stats Card */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-purple-100 rounded-xl">
                  <Tag className="text-purple-600" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800">عدد الأنواع</h2>
                  <p className="text-sm text-gray-600">إجمالي الأنواع المتاحة</p>
                </div>
              </div>
              <div className="text-4xl font-bold text-purple-600">{types.length}</div>
            </div>
          </div>

          {/* Add Button */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 flex items-center justify-end">
            <button
              onClick={() => setNewType({ name: "", isAvailable: true })}
              disabled={isLoading}
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-4 rounded-xl shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center gap-2 font-bold text-lg disabled:opacity-50"
            >
              <Plus size={24} />
              إضافة نوع جديد
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 mb-6 border border-white/30">
          <div className="relative">
            <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="بحث باسم النوع أو الرقم..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pr-12 pl-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-purple-300 focus:border-purple-400 transition-all"
            />
          </div>
        </div>

        {/* Types Table */}
        {isLoading ? (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6">
            <SkeletonLoader type="table" />
          </div>
        ) : filteredTypes.length ? (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 overflow-x-auto">
            <table className="w-full min-w-[800px] table-auto">
              <thead>
                <tr className="bg-gradient-to-r from-purple-50 to-pink-50 border-b-2 border-purple-200">
                  <th className="p-4 text-center font-bold text-purple-800">#ID</th>
                  <th className="p-4 text-right font-bold text-purple-800">اسم النوع</th>
                  <th className="p-4 text-center font-bold text-purple-800">المبيعات</th>
                  <th className="p-4 text-center font-bold text-purple-800">الحالة</th>
                  <th className="p-4 text-center font-bold text-purple-800">الإجراءات</th>
                </tr>
              </thead>
              <tbody>
                {filteredTypes.map((type) => (
                  <tr
                    key={type.id}
                    className={`border-b border-gray-200 hover:bg-white/60 transition-colors ${
                      type.isAvailable ? "" : "bg-red-50/30"
                    }`}
                  >
                    <td className="p-4 text-center">
                      <span className="font-mono font-bold text-purple-600">#{type.id}</span>
                    </td>
                    <td className="text-xl p-2 font-medium">{type.name}</td>
                    <td className="text-2xl p-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <TrendingUp size={22} className="text-green-600" />
                        <span className="font-bold text-green-600">{type.sales}</span>
                      </div>
                    </td>
                    <td className="p-4 text-center">
                      <button
                        onClick={() => handleToggleAvailability(type)}
                        disabled={isLoading}
                        className={`px-4 py-2 rounded-xl text-sm font-bold transition-all duration-300 flex items-center justify-center gap-2 mx-auto ${
                          type.isAvailable
                            ? "bg-green-100 text-green-700 border border-green-200 hover:bg-green-200"
                            : "bg-red-100 text-red-700 border border-red-200 hover:bg-red-200"
                        }`}
                      >
                        {type.isAvailable ? (
                          <>
                            <CheckCircle size={16} />
                            متاح
                          </>
                        ) : (
                          <>
                            <XCircle size={16} />
                            غير متاح
                          </>
                        )}
                      </button>
                    </td>
                    <td className="p-4">
                      <div className="flex gap-2 justify-center">
                        <button
                          onClick={() =>
                            setEditingType({
                              id: type.id,
                              name: type.name,
                              sales: type.sales,
                              isAvailable: type.isAvailable,
                              brandId: type.brandId
                            })
                          }
                          disabled={isLoading}
                          className="bg-amber-500 text-white px-4 py-2 rounded-lg hover:bg-amber-600 hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm font-bold flex items-center gap-2"
                        >
                          <Edit size={14} />
                          تعديل
                        </button>
                        <button
                          onClick={() => handleDeleteType(type.id)}
                          disabled={isLoading}
                          className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm font-bold flex items-center gap-2"
                        >
                          <Trash2 size={14} />
                          حذف
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-16 text-center">
            <Layers className="mx-auto mb-4 text-gray-400" size={64} />
            <p className="text-2xl text-gray-700 mb-2">لا توجد أنواع</p>
            <p className="text-lg text-gray-500">قم بإضافة نوع جديد للبدء</p>
          </div>
        )}

        {/* Create Modal */}
        {newType && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
              <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 rounded-t-2xl flex justify-between items-center">
                <h2 className="text-2xl font-bold">إضافة نوع جديد</h2>
                <button
                  onClick={() => setNewType(null)}
                  className="hover:bg-white/20 p-2 rounded-lg transition"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleCreateType} className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-right font-medium text-gray-700 mb-2">
                      اسم النوع
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={newType.name}
                      onChange={(e) => handleInputChange(e, setNewType, newType)}
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-purple-300 focus:border-purple-400 transition-all"
                      placeholder="أدخل اسم النوع"
                      required
                    />
                  </div>

                  <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-xl">
                    <input
                      type="checkbox"
                      name="isAvailable"
                      id="isAvailable"
                      checked={newType.isAvailable}
                      onChange={(e) => handleInputChange(e, setNewType, newType)}
                      className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                    />
                    <label htmlFor="isAvailable" className="font-medium text-gray-700">
                      متاح للعرض
                    </label>
                  </div>
                </div>

                <div className="flex justify-center gap-4 mt-8">
                  <button
                    type="button"
                    onClick={() => setNewType(null)}
                    className="flex items-center gap-2 bg-gray-400 text-white px-6 py-3 rounded-xl hover:bg-gray-500 font-bold text-lg transition"
                  >
                    <X size={20} />
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-3 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold text-lg"
                  >
                    <Save size={20} />
                    إنشاء
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {editingType && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
              <div className="sticky top-0 bg-gradient-to-r from-amber-600 to-orange-600 text-white p-6 rounded-t-2xl flex justify-between items-center">
                <h2 className="text-2xl font-bold">تعديل النوع #{editingType.id}</h2>
                <button
                  onClick={() => setEditingType(null)}
                  className="hover:bg-white/20 p-2 rounded-lg transition"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleUpdateType} className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-right font-medium text-gray-700 mb-2">
                      اسم النوع
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={editingType.name}
                      onChange={(e) => handleInputChange(e, setEditingType, editingType)}
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-amber-300 focus:border-amber-400 transition-all"
                      placeholder="أدخل اسم النوع"
                      required
                    />
                  </div>

                  <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-xl">
                    <input
                      type="checkbox"
                      name="isAvailable"
                      id="editIsAvailable"
                      checked={editingType.isAvailable}
                      onChange={(e) => handleInputChange(e, setEditingType, editingType)}
                      className="w-5 h-5 text-amber-600 rounded focus:ring-amber-500"
                    />
                    <label htmlFor="editIsAvailable" className="font-medium text-gray-700">
                      متاح للعرض
                    </label>
                  </div>
                </div>

                <div className="flex justify-center gap-4 mt-8">
                  <button
                    type="button"
                    onClick={() => setEditingType(null)}
                    className="flex items-center gap-2 bg-gray-400 text-white px-6 py-3 rounded-xl hover:bg-gray-500 font-bold text-lg transition"
                  >
                    <X size={20} />
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 bg-gradient-to-r from-amber-600 to-orange-600 text-white px-8 py-3 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold text-lg"
                  >
                    <Save size={20} />
                    حفظ التغييرات
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
      {/* Floating Button to Scroll to Top - أضف هذا الزر الجديد */}
      <div className="fixed bottom-6 right-6">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white p-4 rounded-full shadow-2xl hover:shadow-xl hover:scale-110 transition-all duration-300 flex items-center justify-center"
          title="العودة إلى الأعلى"
        >
          <ChevronUp size={24} />
        </button>
      </div>
    </div>
  );
};

export default TypesPage;